import { LabelChip, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TrainingBudgetUnit, TrainingSessionListView } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatCompactCount } from "@/utils";
import { formatMetricNumber } from "../sessionDetail/trainingSessionDetailFormat";
import { sessionDisplayName } from "../trainingSessionFormat";
import {
  compareMetricAxis,
  historyStateKey,
  metricKeyUnion,
  sessionBudgetUnit,
  sessionSeriesColor,
} from "./sessionCompareModel";
import type { SessionCompareController } from "./useSessionCompareController";
import styles from "./SessionCompareChartsSection.module.css";

// 지표 토글 칩의 켜짐/꺼짐 색. 세션 상세 학습 곡선과 같은 정책이다.
const selectedChipColor = 0x2563eb;
const unselectedChipColor = 0x64748b;

type SessionCompareChartsSectionProps = {
  sessions: readonly TrainingSessionListView[];
  slotById: Readonly<Record<string, number>>;
  controller: SessionCompareController;
  selectedKeys: readonly string[];
  onToggleKey: (key: string) => void;
};

// 지표 비교 카드. 세션들의 metric_keys 합집합을 토글 칩으로 둔다. 선택한 지표마다 세션 N개를
// 한 차트에 겹쳐 그린다(MLflow run 비교 관례). 시리즈 색은 범례와 같은 슬롯 팔레트다.
export function SessionCompareChartsSection({
  sessions,
  slotById,
  controller,
  selectedKeys,
  onToggleKey,
}: SessionCompareChartsSectionProps): ReactNode {
  const metricStates = sessions.map((session) => controller.loads[session.id]?.metrics);
  const metricKeys = metricKeyUnion(
    metricStates.map((state) => (state?.status === "success" ? state.data : null)),
  );
  const metricsSettled = metricStates.every(
    (state) => state?.status === "success" || state?.status === "error",
  );
  // 조회가 실패한 Session이 있으면 "기록 없음"으로 단정하지 않는다. 받지 못한 Session에 지표가 있을 수 있다.
  const metricsFailure = metricStates.find((state) => state?.status === "error");
  // 축 단위 판정에는 상세를 로드한 세션들의 예산 단위만 쓴다.
  const budgetUnits: TrainingBudgetUnit[] = sessions.flatMap((session) => {
    const state = controller.loads[session.id]?.detail;
    const unit = sessionBudgetUnit(state?.status === "success" ? state.data : null);
    return unit === null ? [] : [unit];
  });

  return (
    <section className={styles.card} aria-label="지표 비교">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>지표 비교</Text>
        {controller.live ? (
          <span className={styles.cardMeta}>실시간 갱신 중 · 15초 간격</span>
        ) : null}
      </div>
      {metricKeys.length === 0 ? (
        !metricsSettled ? (
          <LoadingState size="sm">Training 지표를 불러오고 있습니다.</LoadingState>
        ) : metricsFailure?.status === "error" ? (
          <EmptyState
            size="sm"
            tone="error"
            action={{ label: "다시 시도", onClick: controller.retry }}
          >
            Training 지표를 불러오지 못했습니다: {metricsFailure.message}
          </EmptyState>
        ) : (
          <EmptyState size="sm">
            기록된 Training 지표가 없습니다. Training이 시작되면 여기에 표시됩니다.
          </EmptyState>
        )
      ) : (
        <>
          <div className={styles.keyChips} role="group" aria-label="비교할 지표 선택">
            {metricKeys.map((key) => {
              const pressed = selectedKeys.includes(key);
              return (
                <LabelChip
                  key={key}
                  color={pressed ? selectedChipColor : unselectedChipColor}
                  size="xs"
                  variant="interactive"
                  aria-pressed={pressed}
                  onClick={() => onToggleKey(key)}
                >
                  {key}
                </LabelChip>
              );
            })}
          </div>
          <div className={styles.charts}>
            {metricKeys
              .filter((key) => selectedKeys.includes(key))
              .map((key) => (
                <CompareMetricChartCard
                  key={key}
                  metricKey={key}
                  sessions={sessions}
                  slotById={slotById}
                  controller={controller}
                  budgetUnits={budgetUnits}
                />
              ))}
          </div>
        </>
      )}
    </section>
  );
}

function CompareMetricChartCard({
  metricKey,
  sessions,
  slotById,
  controller,
  budgetUnits,
}: {
  metricKey: string;
  sessions: readonly TrainingSessionListView[];
  slotById: Readonly<Record<string, number>>;
  controller: SessionCompareController;
  budgetUnits: readonly TrainingBudgetUnit[];
}): ReactNode {
  const axis = compareMetricAxis(metricKey, budgetUnits);
  const historyStates = sessions.map(
    (session) => controller.histories[historyStateKey(session.id, metricKey)],
  );
  const series = sessions.flatMap((session, index) => {
    const state = historyStates[index];
    if (state?.status !== "success" || state.data.points.length === 0) {
      return [];
    }
    return [
      {
        id: session.id,
        label: sessionDisplayName(session),
        color: sessionSeriesColor(slotById[session.id] ?? 0),
        points: state.data.points,
      },
    ];
  });
  const anyLoading = historyStates.some((state) => state?.status === "loading");
  // 그릴 시계열이 없을 때 실패한 조회가 있으면 "기록 없음"이 아니라 실패다.
  const failure = historyStates.find((state) => state?.status === "error");

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHead}>
        <span className={styles.chartTitle}>{metricKey}</span>
        <span className={styles.cardMeta}>X축 {axis.unitLabel}</span>
      </div>
      {axis.mixed ? (
        <p className={styles.warningText}>
          Session 간 X축 단위(에폭/스텝)가 달라 직접 비교에 주의하세요.
        </p>
      ) : null}
      {series.length === 0 ? (
        <div className={`${stateFrameClassName} ${styles.chartState}`}>
          {anyLoading ? (
            <LoadingState size="sm" mode="plain">
              시계열을 불러오고 있습니다.
            </LoadingState>
          ) : failure?.status === "error" ? (
            <EmptyState
              size="sm"
              mode="plain"
              tone="error"
              align="center"
              action={{ label: "다시 시도", onClick: controller.retry }}
            >
              시계열을 불러오지 못했습니다: {failure.message}
            </EmptyState>
          ) : (
            <EmptyState size="sm" mode="plain">
              이 지표의 기록이 아직 없습니다.
            </EmptyState>
          )}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
            <XAxis
              dataKey="step"
              type="number"
              domain={["dataMin", "dataMax"]}
              stroke={CHART_CHROME.axis}
              fontSize={11}
              tickFormatter={(tick) => formatCompactCount(Number(tick))}
              allowDuplicatedCategory={false}
            />
            <YAxis
              width={56}
              domain={["auto", "auto"]}
              stroke={CHART_CHROME.axis}
              fontSize={11}
              tickFormatter={(tick) => formatMetricNumber(Number(tick))}
            />
            <Tooltip
              labelFormatter={(step) => `${axis.unitLabel} ${formatMetricNumber(Number(step))}`}
              formatter={(value, name) => [formatMetricNumber(Number(value)), String(name)]}
              contentStyle={{
                background: CHART_CHROME.tooltipBg,
                borderColor: CHART_CHROME.tooltipBorder,
              }}
            />
            {series.map((item) => (
              // Line 마다 자기 data 를 가지는 recharts 다중 소스 패턴이다. 세션마다 step 표본이
              // 달라도 공유 숫자축 위에 겹쳐 그린다.
              <Line
                key={item.id}
                data={[...item.points]}
                dataKey="value"
                name={item.label}
                type="monotone"
                stroke={item.color}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
